function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/nextjs";
import { StyledAshbyApplicationForm } from "./ashby-application-form.scss";
import { ASHBY_APPLICATION_ROUTE, ASHBY_RESUME_PARSE_ROUTE, CAREERS_CANDIDATES_PRIVACY_POLICY_URL, RESUME_ACCEPTED_FORMATS } from "../../careers-constants";
import { ASHBY_APPLICATION_TEXTS } from "./constants";
import { resolveAshbyUtmData } from "services/careers/ashby-source-tracking-service";
import { trackEvent } from "/services/bigbrain-service";
import { CAREERS_ASHBY_APPLICATION_SUBMITTED } from "/constants/bigbrain-event-types";
const FIELD_TYPE_MAP = {
    String: "text",
    Email: "email",
    Phone: "tel",
    LongText: "textarea",
    RichText: "textarea",
    Number: "number",
    Date: "date",
    Boolean: "checkbox",
    ValueSelect: "select",
    MultiValueSelect: "multiselect",
    File: "file",
    SocialLink: "url",
    Score: "number"
};
const FormField = ({ fieldDef, isRequired, value, onChange, error })=>{
    const { id, path, type, title, selectableValues } = fieldDef;
    const fieldKey = path || id;
    const inputType = FIELD_TYPE_MAP[type] || "text";
    if (inputType === "textarea") {
        return /*#__PURE__*/ _jsx("textarea", {
            id: fieldKey,
            name: fieldKey,
            required: isRequired,
            value: value || "",
            onChange: (event)=>onChange(fieldKey, event.target.value),
            className: `ashby-input ashby-textarea ${error ? "has-error" : ""}`,
            rows: 4
        });
    }
    if (inputType === "checkbox") {
        return /*#__PURE__*/ _jsx("input", {
            id: fieldKey,
            name: fieldKey,
            type: "checkbox",
            checked: !!value,
            onChange: (event)=>onChange(fieldKey, event.target.checked),
            className: "ashby-checkbox"
        });
    }
    if (inputType === "select") {
        return /*#__PURE__*/ _jsxs("select", {
            id: fieldKey,
            name: fieldKey,
            required: isRequired,
            value: value || "",
            onChange: (event)=>onChange(fieldKey, event.target.value),
            className: `ashby-input ashby-select ${error ? "has-error" : ""}`,
            children: [
                /*#__PURE__*/ _jsx("option", {
                    value: "",
                    children: ASHBY_APPLICATION_TEXTS.form.selectPlaceholder
                }),
                selectableValues === null || selectableValues === void 0 ? void 0 : selectableValues.map((opt)=>/*#__PURE__*/ _jsx("option", {
                        value: opt.value,
                        children: opt.label
                    }, opt.value))
            ]
        });
    }
    if (inputType === "multiselect") {
        return /*#__PURE__*/ _jsx("select", {
            id: fieldKey,
            name: fieldKey,
            required: isRequired,
            multiple: true,
            value: value || [],
            onChange: (event)=>{
                const selected = Array.from(event.target.selectedOptions, (opt)=>opt.value);
                onChange(fieldKey, selected);
            },
            className: `ashby-input ashby-select ${error ? "has-error" : ""}`,
            children: selectableValues === null || selectableValues === void 0 ? void 0 : selectableValues.map((opt)=>/*#__PURE__*/ _jsx("option", {
                    value: opt.value,
                    children: opt.label
                }, opt.value))
        });
    }
    if (inputType === "file") {
        return /*#__PURE__*/ _jsx("input", {
            id: fieldKey,
            name: fieldKey,
            type: "file",
            required: isRequired,
            onChange: (event)=>onChange(fieldKey, event.target.files[0]),
            className: `ashby-file-input ${error ? "has-error" : ""}`
        });
    }
    return /*#__PURE__*/ _jsx("input", {
        id: fieldKey,
        name: fieldKey,
        type: inputType,
        required: isRequired,
        value: value || "",
        onChange: (event)=>onChange(fieldKey, event.target.value),
        className: `ashby-input ${error ? "has-error" : ""}`
    });
};
const AshbyApplicationForm = ({ applicationFormDefinition, jobPostingId })=>{
    const [formValues, setFormValues] = useState({});
    const [errors, setErrors] = useState({});
    const [submitState, setSubmitState] = useState("idle"); // idle | submitting | success | error
    const [submitError, setSubmitError] = useState("");
    const [autofillState, setAutofillState] = useState("idle"); // idle | parsing | success | error
    const [resumeFile, setResumeFile] = useState(null);
    const handleChange = useCallback((fieldId, value)=>{
        setFormValues((prev)=>_object_spread_props(_object_spread({}, prev), {
                [fieldId]: value
            }));
        setErrors((prev)=>_object_spread_props(_object_spread({}, prev), {
                [fieldId]: false
            }));
    }, []);
    const findFieldPath = useCallback((titlePattern)=>{
        for (const section of (applicationFormDefinition === null || applicationFormDefinition === void 0 ? void 0 : applicationFormDefinition.sections) || []){
            for (const { field } of section.fields || []){
                var _field_title;
                if ((_field_title = field.title) === null || _field_title === void 0 ? void 0 : _field_title.toLowerCase().includes(titlePattern)) {
                    return field.path || field.id;
                }
            }
        }
        return null;
    }, [
        applicationFormDefinition
    ]);
    const sections = (applicationFormDefinition === null || applicationFormDefinition === void 0 ? void 0 : applicationFormDefinition.sections) || [];
    const handleAutofillFromResume = useCallback(function() {
        var _ref = _async_to_generator(function*(file) {
            if (!file) return;
            setAutofillState("parsing");
            setResumeFile(file);
            try {
                const formData = new FormData();
                formData.append("resume", file);
                formData.append("jobPostingId", jobPostingId);
                const response = yield fetch(ASHBY_RESUME_PARSE_ROUTE, {
                    method: "POST",
                    body: formData
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const result = yield response.json();
                if (result.success) {
                    var _result_parsedData_name, _result_parsedData, _result_parsedData_email, _result_parsedData1;
                    setFormValues((prev)=>{
                        var _parsedData_name, _parsedData_email, _parsedData_phone, _parsedData_location, _parsedData_linkedin;
                        const updated = _object_spread_props(_object_spread({}, prev), {
                            "_systemfield_resume": file
                        });
                        const { parsedData } = result;
                        if (parsedData === null || parsedData === void 0 ? void 0 : (_parsedData_name = parsedData.name) === null || _parsedData_name === void 0 ? void 0 : _parsedData_name.trim()) updated["_systemfield_name"] = parsedData.name;
                        if (parsedData === null || parsedData === void 0 ? void 0 : (_parsedData_email = parsedData.email) === null || _parsedData_email === void 0 ? void 0 : _parsedData_email.trim()) updated["_systemfield_email"] = parsedData.email;
                        if (parsedData === null || parsedData === void 0 ? void 0 : (_parsedData_phone = parsedData.phone) === null || _parsedData_phone === void 0 ? void 0 : _parsedData_phone.trim()) updated["_systemfield_phone"] = parsedData.phone;
                        if (parsedData === null || parsedData === void 0 ? void 0 : (_parsedData_location = parsedData.location) === null || _parsedData_location === void 0 ? void 0 : _parsedData_location.trim()) updated["_systemfield_location"] = parsedData.location;
                        if (parsedData === null || parsedData === void 0 ? void 0 : (_parsedData_linkedin = parsedData.linkedin) === null || _parsedData_linkedin === void 0 ? void 0 : _parsedData_linkedin.trim()) {
                            const linkedinPath = findFieldPath("linkedin");
                            if (linkedinPath) updated[linkedinPath] = parsedData.linkedin;
                        }
                        return updated;
                    });
                    const hasExtractedFields = ((_result_parsedData = result.parsedData) === null || _result_parsedData === void 0 ? void 0 : (_result_parsedData_name = _result_parsedData.name) === null || _result_parsedData_name === void 0 ? void 0 : _result_parsedData_name.trim()) || ((_result_parsedData1 = result.parsedData) === null || _result_parsedData1 === void 0 ? void 0 : (_result_parsedData_email = _result_parsedData1.email) === null || _result_parsedData_email === void 0 ? void 0 : _result_parsedData_email.trim());
                    setAutofillState(hasExtractedFields ? "success" : "partial");
                } else {
                    setFormValues((prev)=>_object_spread_props(_object_spread({}, prev), {
                            "_systemfield_resume": file
                        }));
                    setAutofillState("error");
                }
            } catch (error) {
                Sentry.captureException(error);
                setFormValues((prev)=>_object_spread_props(_object_spread({}, prev), {
                        "_systemfield_resume": file
                    }));
                setAutofillState("error");
            }
        });
        return function(file) {
            return _ref.apply(this, arguments);
        };
    }(), [
        jobPostingId,
        findFieldPath
    ]);
    const validate = useCallback(()=>{
        const newErrors = {};
        sections.forEach((section)=>{
            var _section_fields;
            (_section_fields = section.fields) === null || _section_fields === void 0 ? void 0 : _section_fields.forEach(({ isRequired, field })=>{
                const fieldKey = field.path || field.id;
                if (isRequired) {
                    const val = formValues[fieldKey];
                    if (val === undefined || val === null || val === "") {
                        newErrors[fieldKey] = true;
                    }
                }
            });
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [
        sections,
        formValues
    ]);
    const fileFieldPaths = useMemo(()=>{
        const paths = new Set();
        sections.forEach((section)=>{
            var _section_fields;
            (_section_fields = section.fields) === null || _section_fields === void 0 ? void 0 : _section_fields.forEach(({ field })=>{
                if (field.type === "File") {
                    paths.add(field.path || field.id);
                }
            });
        });
        return paths;
    }, [
        sections
    ]);
    const visibleFieldKeys = useMemo(()=>{
        const keys = new Set();
        sections.forEach((section)=>{
            var _section_fields;
            (_section_fields = section.fields) === null || _section_fields === void 0 ? void 0 : _section_fields.forEach(({ field })=>{
                keys.add(field.path || field.id);
            });
        });
        return keys;
    }, [
        sections
    ]);
    const handleSubmit = useCallback(function() {
        var _ref = _async_to_generator(function*(event) {
            event.preventDefault();
            if (!validate()) return;
            setSubmitState("submitting");
            setSubmitError("");
            try {
                const textFieldValues = {};
                const fileFields = {};
                Object.entries(formValues).forEach(([key, value])=>{
                    if (!visibleFieldKeys.has(key)) return;
                    const isFile = value && typeof value === 'object' && value.name !== undefined && value.size !== undefined;
                    if (fileFieldPaths.has(key) && isFile) {
                        fileFields[key] = value;
                    } else if (!fileFieldPaths.has(key)) {
                        textFieldValues[key] = value;
                    }
                });
                const formData = new FormData();
                formData.append("jobPostingId", jobPostingId);
                const fieldSubmissions = [
                    ...Object.entries(textFieldValues).map(([path, value])=>({
                            path,
                            value
                        })),
                    ...Object.entries(fileFields).map(([path])=>({
                            path,
                            value: path
                        }))
                ];
                formData.append("applicationForm", JSON.stringify({
                    fieldSubmissions
                }));
                Object.entries(fileFields).forEach(([fieldPath, file])=>{
                    formData.append(fieldPath, file);
                });
                const utmData = resolveAshbyUtmData();
                if (Object.keys(utmData).length > 0) {
                    formData.append("utmData", JSON.stringify(utmData));
                }
                const response = yield fetch(ASHBY_APPLICATION_ROUTE, {
                    method: "POST",
                    body: formData
                });
                if (!response.ok) {
                    Sentry.captureMessage("Ashby application submission failed", {
                        level: "error",
                        extra: {
                            status: response.status,
                            statusText: response.statusText
                        }
                    });
                    setSubmitState("error");
                    setSubmitError(`Server error: ${response.status} ${response.statusText}`);
                    return;
                }
                const result = yield response.json();
                if (result.success) {
                    setSubmitState("success");
                    trackEvent(CAREERS_ASHBY_APPLICATION_SUBMITTED, {
                        info1: jobPostingId,
                        kind: utmData.utm_source
                    });
                } else {
                    var _result_errors;
                    setSubmitState("error");
                    setSubmitError(((_result_errors = result.errors) === null || _result_errors === void 0 ? void 0 : _result_errors.join(", ")) || result.error || "Failed to submit application");
                }
            } catch (error) {
                Sentry.captureException(error);
                setSubmitState("error");
                setSubmitError("Failed to submit application. Please try again.");
            }
        });
        return function(event) {
            return _ref.apply(this, arguments);
        };
    }(), [
        jobPostingId,
        formValues,
        validate,
        fileFieldPaths,
        visibleFieldKeys
    ]);
    if (!(applicationFormDefinition === null || applicationFormDefinition === void 0 ? void 0 : applicationFormDefinition.sections)) {
        return /*#__PURE__*/ _jsx(StyledAshbyApplicationForm, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "fallback-message",
                children: /*#__PURE__*/ _jsx("p", {
                    children: ASHBY_APPLICATION_TEXTS.fallbackUnavailable
                })
            })
        });
    }
    if (submitState === "success") {
        return /*#__PURE__*/ _jsx(StyledAshbyApplicationForm, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: "success-message",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        children: ASHBY_APPLICATION_TEXTS.success.heading
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: ASHBY_APPLICATION_TEXTS.success.body
                    })
                ]
            })
        });
    }
    const autofillTexts = ASHBY_APPLICATION_TEXTS.autofill;
    const formTexts = ASHBY_APPLICATION_TEXTS.form;
    return /*#__PURE__*/ _jsx(StyledAshbyApplicationForm, {
        children: /*#__PURE__*/ _jsxs("form", {
            onSubmit: handleSubmit,
            noValidate: true,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "autofill-section",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "autofill-header",
                            children: [
                                /*#__PURE__*/ _jsx("h4", {
                                    children: autofillTexts.heading
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: autofillTexts.subheading
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "autofill-upload",
                            children: [
                                /*#__PURE__*/ _jsx("input", {
                                    type: "file",
                                    id: "autofill-resume",
                                    accept: RESUME_ACCEPTED_FORMATS,
                                    onChange: (event)=>{
                                        const file = event.target.files[0];
                                        if (file) {
                                            handleAutofillFromResume(file);
                                        }
                                    },
                                    className: "autofill-file-input",
                                    disabled: autofillState === "parsing",
                                    style: {
                                        display: "none"
                                    }
                                }),
                                /*#__PURE__*/ _jsx("label", {
                                    htmlFor: "autofill-resume",
                                    className: `autofill-upload-button ${autofillState === "parsing" ? "loading" : ""}`,
                                    children: autofillState === "parsing" ? autofillTexts.parsing : autofillTexts.chooseFile
                                }),
                                resumeFile && /*#__PURE__*/ _jsxs("div", {
                                    className: "autofill-status",
                                    children: [
                                        (autofillState === "success" || autofillState === "partial") && /*#__PURE__*/ _jsx("span", {
                                            className: "status-success",
                                            children: autofillTexts.statusSuccess
                                        }),
                                        autofillState === "error" && /*#__PURE__*/ _jsx("span", {
                                            className: "status-error",
                                            children: autofillTexts.statusError
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "file-name",
                                            children: resumeFile.name
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("hr", {
                    className: "section-divider"
                }),
                sections.map((section, sectionIdx)=>{
                    var _section_fields;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "form-section",
                        children: [
                            section.title && /*#__PURE__*/ _jsx("h4", {
                                className: "section-title",
                                children: section.title
                            }),
                            section.descriptionPlain && /*#__PURE__*/ _jsx("p", {
                                className: "section-description",
                                children: section.descriptionPlain
                            }),
                            (_section_fields = section.fields) === null || _section_fields === void 0 ? void 0 : _section_fields.map(({ isRequired, field, descriptionPlain })=>{
                                const fieldKey = field.path || field.id;
                                const isResumeField = fieldKey === "_systemfield_resume";
                                // Show resume field status if it was populated by autofill
                                // Check if it's a file object (works in both SSR and client contexts)
                                const isFileValue = formValues[fieldKey] && typeof formValues[fieldKey] === 'object' && formValues[fieldKey].name && formValues[fieldKey].size !== undefined;
                                if (isResumeField && isFileValue) {
                                    return /*#__PURE__*/ _jsxs("div", {
                                        className: "form-field resume-field-populated",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                htmlFor: fieldKey,
                                                className: isRequired ? "required" : "",
                                                children: field.title
                                            }),
                                            descriptionPlain && /*#__PURE__*/ _jsx("span", {
                                                className: "field-description",
                                                children: descriptionPlain
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "resume-file-status",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "file-icon"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "file-name",
                                                        children: formValues[fieldKey].name
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        type: "button",
                                                        className: "change-file-button",
                                                        onClick: ()=>document.getElementById(`change-resume-${fieldKey}`).click(),
                                                        children: formTexts.resumeChangeButton
                                                    }),
                                                    /*#__PURE__*/ _jsx("input", {
                                                        type: "file",
                                                        id: `change-resume-${fieldKey}`,
                                                        accept: RESUME_ACCEPTED_FORMATS,
                                                        onChange: (event)=>{
                                                            const file = event.target.files[0];
                                                            if (file) {
                                                                handleChange(fieldKey, file);
                                                            }
                                                        },
                                                        style: {
                                                            display: "none"
                                                        }
                                                    })
                                                ]
                                            })
                                        ]
                                    }, fieldKey);
                                }
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "form-field",
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            htmlFor: fieldKey,
                                            className: isRequired ? "required" : "",
                                            children: field.title
                                        }),
                                        descriptionPlain && /*#__PURE__*/ _jsx("span", {
                                            className: "field-description",
                                            children: descriptionPlain
                                        }),
                                        /*#__PURE__*/ _jsx(FormField, {
                                            fieldDef: field,
                                            isRequired: isRequired,
                                            value: formValues[fieldKey],
                                            onChange: handleChange,
                                            error: errors[fieldKey]
                                        }),
                                        errors[fieldKey] && /*#__PURE__*/ _jsx("span", {
                                            className: "error-text",
                                            children: formTexts.requiredError
                                        })
                                    ]
                                }, fieldKey);
                            })
                        ]
                    }, sectionIdx);
                }),
                submitState === "error" && /*#__PURE__*/ _jsx("div", {
                    className: "submit-error",
                    children: submitError
                }),
                /*#__PURE__*/ _jsx("hr", {
                    className: "privacy-divider"
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "privacy-notice",
                    children: [
                        formTexts.privacyNotice,
                        " ",
                        /*#__PURE__*/ _jsx("a", {
                            href: CAREERS_CANDIDATES_PRIVACY_POLICY_URL,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            children: formTexts.privacyLinkText
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("button", {
                    type: "submit",
                    className: "submit-button",
                    disabled: submitState === "submitting",
                    children: submitState === "submitting" ? formTexts.submitInProgress : formTexts.submitIdle
                })
            ]
        })
    });
};
export default AshbyApplicationForm;
